import React from 'react';
import {
  Plugins,
  Atom,
  Expressions,
  type AtomRenderProps,
} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TTypeConstructor,
  TTypeApplication,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  isFormFileUpload,
  isUserFormFileInput,
} from '@packages/utilities/elementTypeUtils';
import {findElementDescendant} from '@packages/utilities/platform';
import {ATTR_NAME_PATH, FILE_DEFAULT_LABEL} from './constants';
import {TEMP_STATE_PATH} from '@packages/utilities/expression-path-constants';
import {extraAtomFields} from '@packages/shared/constants/Atoms';
import {
  EvaluatedNodeData,
  getChildren,
  getInPojo,
} from '@packages/utilities/data-utils';

const isFileUploadInputElement = (element: ExpressionElement) =>
  isFormFileUpload(element) || isUserFormFileInput(element);

// The file input is nested inside FormFileUploadDefault, not a direct
// child of the wrapper, so each wrapped child's subtree must be searched.
const findFileUploadInputElement = (
  data: EvaluatedNodeData
): ExpressionElement | undefined => {
  for (const child of getChildren(data)) {
    const childElement = getInPojo(child, ['props', 'element']) as
      | ExpressionElement
      | undefined;
    if (!childElement) continue;
    const found = findElementDescendant(childElement, isFileUploadInputElement);
    if (found) return found;
  }
  return undefined;
};

const FormFileUploadWrapper = Atom.createRender(
  ({
    attributes,
    classes,
    generateAttributes,
    data,
    context,
    renderChild,
  }: AtomRenderProps) => {
    const wrapperClasses = classes.push('w-file-upload');
    const inputElement = findFileUploadInputElement(data);
    const formFileUploadState = inputElement
      ? Expressions.getElementDataAt(
          inputElement,
          TEMP_STATE_PATH,
          'Enum',
          'default'
        )
      : 'default';
    const name = inputElement
      ? Expressions.getElementDataAt(
          inputElement,
          ATTR_NAME_PATH,
          'Text',
          FILE_DEFAULT_LABEL
        )
      : FILE_DEFAULT_LABEL;
    const updatedContext = context
      .set('for', name)
      .set('formFileUploadState', formFileUploadState);

    return (
      <div {...generateAttributes(attributes, wrapperClasses)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, updatedContext)
        )}
      </div>
    );
  }
);

const config = Atom.create({
  displayName: 'File Upload',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('Upload')),
  },
  showEmpty: true,
  constraints: {
    wraps: [
      'FormFileUploadDefault',
      'FormFileUploadUploading',
      'FormFileUploadSuccess',
      'FormFileUploadError',
    ],
    ancestors: [
      {
        is: 'FormForm',
        rule: 'ExactlyOne',
      },
    ],
    descendants: [
      {is: 'FormTextInput', rule: 'Forbid'},
      {is: 'FormTextarea', rule: 'Forbid'},
      {is: 'FormSelect', rule: 'Forbid'},
      {is: 'FormRadioWrapper', rule: 'Forbid'},
      {is: 'FormCheckboxWrapper', rule: 'Forbid'},
      {is: 'FormButton', rule: 'Forbid'},
      {is: 'FormWrapper', rule: 'Forbid'},
      {is: 'FormFileUploadWrapper', rule: 'Forbid'},
      {is: 'FormReCaptcha', rule: 'Forbid'},
    ],
  },
  render: FormFileUploadWrapper,
  dataType: TTypeApplication({
    con: TTypeConstructor(
      qualify(Plugins.name('Form'), typeName('FormFileUploadChildren'))
    ),
    arg: extraAtomFields,
  }),
  showSettingsInDescendants: true,
});

export default config;
